/**
 * STOCK CALCULATION SERVICE
 * Denormalized stockData warm/recalc (product×location map) after lot projection.
 * Inventory write truth = InventoryMovement (ADR-0016).
 * Operational qty: getStockData / projectStockFromLots — not blob dual-read.
 */

import { getLotsForProduct } from '../utils/lotSystem';
import { getCurrentBusinessId, businessDataStorageKey } from '../utils/sharedCore';
import { readBusinessPayloadFieldsSync } from '../application/read/syncBusinessPayloadRead';
import { getBusinessPayloadRepository } from '../app/di/container';
import { cacheGet, cacheSet } from './cacheLayer';
import { projectStockFromLots } from '../domain/inventory/stock';
import {
  publishLegacyWindowEvent,
  publishForceStockRefresh
} from '../infrastructure/events/legacyEventBridge';
import { isCoordinatedStockRecalculationActive } from '../domain/inventory/recalculation';
import { isReadModelReady, syncEntities } from './cacheReadModel';

function loadMergedBusinessPayload(businessId) {
  if (!businessId) return null;
  return readBusinessPayloadFieldsSync(businessId);
}

function lotsFromPayload(parsed) {
  if (!parsed?.lots) return [];
  return Array.isArray(parsed.lots) ? parsed.lots : Object.values(parsed.lots);
}

/** Mark denormalized stockData row as non-authoritative projection cache. */
function asStockProjectionCacheEntry(stockData) {
  return {
    ...stockData,
    kind: 'projection_cache',
    inventoryTruth: false,
    source: 'lot_projection',
    lastCalculated: new Date().toISOString()
  };
}

function warmInstantCacheStockData(businessId) {
  if (isCoordinatedStockRecalculationActive()) return;
  try {
    const parsed = loadMergedBusinessPayload(businessId);
    if (parsed && isReadModelReady()) {
      syncEntities({ stockData: parsed.stockData || {} });
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[STOCK CALC] Instant cache warm failed:', error?.message || error);
    }
  }
}

function emitStockRecalcEvents(productId, location, stockData, source = 'lotRecalculation') {
  const detail = {
    productId,
    location,
    stockData,
    source,
    timestamp: Date.now()
  };
  publishLegacyWindowEvent('stockUpdated', detail);
  publishLegacyWindowEvent('inventoryUpdated', detail);
}

/**
 * Project stock for product×location from lots (domain owner formula).
 */
export const calculateStockFromLots = (productId, location = null) => {
  try {
    const lots = getLotsForProduct(productId, location);
    return projectStockFromLots(productId, location, lots);
  } catch (error) {
    console.error('[STOCK CALC] calculateStockFromLots failed:', error);
    return {
      productId,
      location,
      stockIn: 0,
      stockOut: 0,
      stockNow: 0,
      value: 0,
      lotsCount: 0,
      activeLotsCount: 0,
      lastCalculated: new Date().toISOString(),
      source: 'lot_projection',
      error: error.message
    };
  }
};

/**
 * Recalculate and persist denorm stockData entry for one product×location.
 */
export const recalculateAndUpdateStock = async (productId, location = null) => {
  try {
    let allStockAggregateForEvents = null;
    const stockData = calculateStockFromLots(productId, location);

    const businessId = getCurrentBusinessId();
    if (!businessId) {
      return { success: false, error: 'No business ID found' };
    }

    const stockKey = location ? `${productId}_${location}` : `${productId}_all`;

    await getBusinessPayloadRepository().mutatePayload(
      businessId,
      (parsed) => {
        if (!parsed.stockData) parsed.stockData = {};
        parsed.stockData[stockKey] = asStockProjectionCacheEntry(stockData);
        if (location) {
          const allStockKey = `${productId}_all`;
          const allStockData = calculateStockFromLots(productId, null);
          parsed.stockData[allStockKey] = asStockProjectionCacheEntry(allStockData);
          allStockAggregateForEvents = allStockData;
        }
        return parsed;
      },
      { authoritativeFields: ['stockData'] }
    );

    emitStockRecalcEvents(productId, location, stockData);
    if (location && allStockAggregateForEvents) {
      emitStockRecalcEvents(productId, null, allStockAggregateForEvents);
    }

    warmInstantCacheStockData(businessId);
    return { success: true, stockData };
  } catch (error) {
    console.error('[STOCK CALC] recalculateAndUpdateStock failed:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Batch denorm stockData write for several product×location entries.
 * @param {{ productId: string|number, location?: string|null }[]} entries
 */
export const recalculateAndUpdateStocksBatch = async (entries = []) => {
  try {
    const businessId = getCurrentBusinessId();
    if (!businessId) {
      return { success: false, error: 'No business ID found' };
    }

    const uniqueEntries = [];
    const seen = new Set();
    for (const entry of entries) {
      if (!entry?.productId) continue;
      const location = entry.location ?? null;
      const key = `${entry.productId}|${location || ''}`;
      if (seen.has(key)) continue;
      seen.add(key);
      uniqueEntries.push({ productId: entry.productId, location });
    }

    if (uniqueEntries.length === 0) {
      return { success: true, results: [] };
    }

    const stockPatches = {};
    const calculated = [];

    for (const { productId, location } of uniqueEntries) {
      const stockKey = location ? `${productId}_${location}` : `${productId}_all`;
      const stockData = calculateStockFromLots(productId, location);
      stockPatches[stockKey] = asStockProjectionCacheEntry(stockData);
      calculated.push({ productId, location, stockKey, stockData });

      if (location) {
        const allStockKey = `${productId}_all`;
        stockPatches[allStockKey] = asStockProjectionCacheEntry(
          calculateStockFromLots(productId, null)
        );
      }
    }

    await getBusinessPayloadRepository().mutatePayload(
      businessId,
      (parsed) => {
        if (!parsed.stockData) parsed.stockData = {};
        Object.assign(parsed.stockData, stockPatches);
        return parsed;
      },
      { authoritativeFields: ['stockData'] }
    );

    warmInstantCacheStockData(businessId);

    for (const { productId, location, stockData } of calculated) {
      publishLegacyWindowEvent('stockUpdated', {
        productId,
        location,
        stockData,
        source: 'lotRecalculationBatch',
        forceRefresh: true,
        timestamp: Date.now()
      });
    }

    publishForceStockRefresh({
      source: 'lotRecalculationBatch',
      timestamp: Date.now()
    });

    return { success: true, results: calculated };
  } catch (error) {
    console.error('[STOCK CALC] recalculateAndUpdateStocksBatch failed:', error);
    return { success: false, error: error.message };
  }
};

export const recalculateAndUpdateMultipleStocks = async (productIds, location = null) => {
  const entries = (productIds || []).map((productId) => ({ productId, location }));
  return recalculateAndUpdateStocksBatch(entries);
};

/**
 * Operational qty — always lot projection (memory TTL). Never blob stockData truth.
 */
export const getStockData = (productId, location = null, forceRefresh = false) => {
  try {
    const cacheKey = `stock:${productId}:${location || '*'}`;
    if (!forceRefresh) {
      const cached = cacheGet(cacheKey);
      if (cached) return cached;
    }
    const result = calculateStockFromLots(productId, location);
    cacheSet(cacheKey, result, 10000);
    return result;
  } catch (error) {
    console.error('[STOCK CALC] getStockData failed:', error);
    const result = calculateStockFromLots(productId, location);
    cacheSet(`stock:${productId}:${location || '*'}`, result, 10000);
    return result;
  }
};

/** @deprecated Alias kept for call-sites; prefer getStockData. */
export const getStockDataHotPath = getStockData;

/**
 * Optional: compare lot projection vs denorm blob entry (cache drift only).
 */
export const validateStockConsistency = (productId, location = null) => {
  try {
    const calculatedStock = calculateStockFromLots(productId, location);
    const businessId = getCurrentBusinessId();
    const stockKey = location ? `${productId}_${location}` : `${productId}_all`;
    const blobEntry =
      businessId && location != null
        ? loadMergedBusinessPayload(businessId)?.stockData?.[stockKey]
        : null;

    if (!blobEntry) {
      return { isConsistent: true, calculatedStock, cachedStock: null, note: 'no_denorm_entry' };
    }

    const isConsistent =
      Number(calculatedStock.stockNow) === Number(blobEntry.stockNow) &&
      Number(calculatedStock.stockIn) === Number(blobEntry.stockIn) &&
      Number(calculatedStock.stockOut) === Number(blobEntry.stockOut) &&
      Math.abs(Number(calculatedStock.value) - Number(blobEntry.value)) < 0.01;

    if (!isConsistent) {
      void recalculateAndUpdateStock(productId, location);
    }

    return { isConsistent, calculatedStock, cachedStock: blobEntry };
  } catch (error) {
    return { isConsistent: false, error: error.message };
  }
};

export const forceRefreshStock = async (productId, location = null) => {
  try {
    const businessId = getCurrentBusinessId();
    if (businessId) {
      const stockKey = location ? `${productId}_${location}` : `${productId}_all`;
      const allStockKey = `${productId}_all`;
      await getBusinessPayloadRepository().mutatePayload(
        businessId,
        (parsed) => {
          if (!parsed.stockData) parsed.stockData = {};
          delete parsed.stockData[stockKey];
          if (location) delete parsed.stockData[allStockKey];
          return parsed;
        },
        { authoritativeFields: ['stockData'] }
      );
    }
    return await recalculateAndUpdateStock(productId, location);
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/** Dev/console: lots vs denorm stockData vs fresh projection */
export const debugStockSystem = (productId, location = null) => {
  try {
    const businessId = getCurrentBusinessId();
    if (!businessDataStorageKey(businessId)) {
      return { error: 'No business storage key' };
    }
    const parsed = loadMergedBusinessPayload(businessId);
    if (!parsed) return { error: 'No business data' };

    const lots = lotsFromPayload(parsed).filter(
      (lot) =>
        String(lot.productId) === String(productId) &&
        (location == null || lot.location === location)
    );
    const stockKey = location ? `${productId}_${location}` : `${productId}_all`;
    const cachedStock = parsed.stockData?.[stockKey];
    const freshStock = calculateStockFromLots(productId, location);
    const isConsistent =
      cachedStock &&
      Number(cachedStock.stockNow) === Number(freshStock.stockNow) &&
      Number(cachedStock.stockIn) === Number(freshStock.stockIn) &&
      Number(cachedStock.stockOut) === Number(freshStock.stockOut);

    return { lots, cachedStock, freshStock, isConsistent };
  } catch (error) {
    return { error: error.message };
  }
};

/** Dev: rebuild all denorm stockData keys from live lots */
export const forceRecalculateAllStocks = async () => {
  try {
    const businessId = getCurrentBusinessId();
    if (!businessId) return { success: false, error: 'No business ID found' };

    const parsed = loadMergedBusinessPayload(businessId);
    if (!parsed) return { success: false, error: 'No business data found' };

    const lots = lotsFromPayload(parsed);
    const entries = [];
    const seen = new Set();
    for (const lot of lots) {
      if (!lot?.productId || !lot.location) continue;
      const key = `${lot.productId}|${lot.location}`;
      if (seen.has(key)) continue;
      seen.add(key);
      entries.push({ productId: lot.productId, location: lot.location });
    }

    return recalculateAndUpdateStocksBatch(entries);
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Browser console — development only (ADR-0016 prod hygiene)
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.debugStock = debugStockSystem;
  window.forceRefreshStock = forceRefreshStock;
  window.getStockData = getStockData;
  window.forceRecalculateAllStocks = forceRecalculateAllStocks;
}
